import { createColumnHelper, Row, Table } from '@tanstack/react-table';
import {
  renderHeaderCell,
  renderTableCell,
  SortHeader,
} from './DataTableColumn';
import { fullName } from '@shared';
import { Tooltip } from '@mui/material';
import { Checkbox } from '@/components/ui/checkbox';
import { ActionTags } from './ActionTags';
import { SelectAllCheckbox } from './SelectAllCheckbox';

const columnHelper = createColumnHelper<Client>();

export const createOpportunityClientsTableColumns = (
  engagementType: ClientEmailEngagement,
) => {
  return [
    {
      id: 'select',
      header: ({ table }: { table: Table<Client> }) => (
        <SelectAllCheckbox table={table} />
      ),
      cell: ({ row }: { row: Row<Client> }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label="Select row"
          disabled={!row.original.clientEmail}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    columnHelper.accessor(row => fullName(row), {
      id: 'clientName',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            children="Client Name"
          />,
          'ml-2',
        ),
      cell: info => renderTableCell(info.getValue()),
      enableHiding: false,
    }),

    columnHelper.accessor('clientEmail', {
      id: 'email',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            children="Email"
          />,
        ),
      cell: info => renderTableCell(info.getValue()),
    }),
    columnHelper.accessor(
      row => row.latestEmailReminders?.[engagementType] || null,
      {
        id: 'lastReminder',
        header: ({ column }) =>
          renderHeaderCell(
            <SortHeader
              column={column}
              children="Last Reminder Sent"
            />,
          ),
        sortDescFirst: true,
        sortingFn: (rowA, rowB) => {
          // Get reminder dates for both rows
          const dateA = rowA.getValue('lastReminder');
          const dateB = rowB.getValue('lastReminder');

          // If row A has no reminder date, it should appear after row B
          if (!dateA) return 1;

          // If row B has no reminder date, it should appear after row A
          if (!dateB) return -1;

          // Compare timestamps (newer dates first)
          // Converting both dates to timestamps using getTime()
          return (dateB as Date).getTime() - (dateA as Date).getTime();
        },
        cell: info => {
          const date = info.getValue();
          if (!date) return renderTableCell('Never');

          const daysAgo = Math.floor(
            (new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
          );
          const daysText =
            daysAgo === 0
              ? 'Today'
              : daysAgo === 1
                ? 'Yesterday'
                : `${daysAgo} days ago`;

          return renderTableCell(
            <Tooltip
              title={<span className="text-xs">{date.toLocaleString()}</span>}
            >
              <span>{daysText}</span>
            </Tooltip>,
          );
        },
      },
    ),
    columnHelper.display({
      id: 'statuses',
      header: () => renderHeaderCell('Statuses'),
      cell: ({ row }) => {
        const client = row.original;
        return (
          <ActionTags
            clientTags={client.clientTags}
            client={client}
          />
        );
      },
    }),
    columnHelper.accessor(row => row.clientAddedDateTime, {
      id: 'addedDate',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            children="Added Date"
          />,
        ),
      cell: info => {
        const date = info.getValue();
        return renderTableCell(
          <Tooltip
            title={<span className="text-xs">{date.toLocaleTimeString()}</span>}
          >
            <span>{date.toLocaleDateString()}</span>
          </Tooltip>,
          'ml-2',
        );
      },
    }),
  ];
};
